import { useState, useEffect } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, ShieldCheck, Power } from "lucide-react";
import { adminGet, adminPost, adminPut, adminDelete } from "../lib/adminApi";

export const PERMS = [
  { key: "stats", label: "Statistiques" },
  { key: "quotes", label: "Devis & demandes" },
  { key: "artisans", label: "Artisans" },
  { key: "properties", label: "Biens & locataires" },
  { key: "receipts", label: "Loyers & reçus" },
  { key: "tenants", label: "Comptes locataires" },
  { key: "realizations", label: "Réalisations" },
  { key: "settings", label: "Paramètres" },
  { key: "users", label: "Gestion des utilisateurs" },
];

const input = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-white/40";

function PermToggle({ on, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${on ? "bg-emerald-500/25 text-emerald-300" : "bg-white/10 text-white/50 hover:bg-white/15"}`}>
      <span className={`h-2 w-2 rounded-full ${on ? "bg-emerald-400" : "bg-white/30"}`} /> {label}
    </button>
  );
}

export default function AdminUsers() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [pwEdit, setPwEdit] = useState({});

  const load = () => adminGet("/admin/staff").then(setList).catch(() => toast.error("Erreur de chargement"));
  useEffect(() => { load(); }, []);

  const toggleNew = (k) =>
    setForm((f) => ({ ...f, permissions: f.permissions.includes(k) ? f.permissions.filter((x) => x !== k) : [...f.permissions, k] }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Nom, email et mot de passe (min 6) requis");
    setSaving(true);
    try {
      await adminPost("/admin/staff", form);
      toast.success("Utilisateur créé");
      setForm({ name: "", email: "", password: "", permissions: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  const toggleUserPerm = async (u, k) => {
    const perms = u.permissions.includes(k) ? u.permissions.filter((x) => x !== k) : [...u.permissions, k];
    await adminPut(`/admin/staff/${u.id}`, { permissions: perms }).catch(() => toast.error("Échec"));
    load();
  };
  const toggleActive = async (u) => {
    await adminPut(`/admin/staff/${u.id}`, { active: !u.active }).catch(() => toast.error("Échec"));
    load();
  };
  const savePw = async (u) => {
    const pw = pwEdit[u.id];
    if (!pw || pw.length < 6) return toast.error("Mot de passe : min 6 caractères");
    await adminPut(`/admin/staff/${u.id}`, { password: pw }).then(() => { toast.success("Mot de passe mis à jour"); setPwEdit((s) => ({ ...s, [u.id]: "" })); }).catch(() => toast.error("Échec"));
  };
  const remove = async (u) => {
    await adminDelete(`/admin/staff/${u.id}`).then(() => { toast.success("Supprimé"); load(); }).catch(() => toast.error("Échec"));
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-white"><ShieldCheck size={18} /> Utilisateurs & accès</h2>
      <p className="mt-1 text-sm text-white/50">Créez des comptes et activez uniquement les accès nécessaires (boutons verts = autorisé).</p>

      {/* Créer */}
      <form onSubmit={create} className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} placeholder="Nom complet" />
          <input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} placeholder="Email" />
          <input data-testid="staff-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} placeholder="Mot de passe (min 6)" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {PERMS.map((p) => (
            <PermToggle key={p.key} on={form.permissions.includes(p.key)} label={p.label} onClick={() => toggleNew(p.key)} testid={`staff-new-perm-${p.key}`} />
          ))}
        </div>
        <button type="submit" disabled={saving} data-testid="staff-create-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:bg-white/90 disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Créer l'utilisateur
        </button>
      </form>

      {/* Liste */}
      <div className="mt-5 space-y-3">
        {!list ? <p className="text-white/50">Chargement…</p> : list.length === 0 ? (
          <p className="text-white/50">Aucun utilisateur. L'administrateur principal a toujours tous les accès.</p>
        ) : list.map((u) => (
          <div key={u.id} data-testid={`staff-${u.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-medium text-white">{u.name}</span>
                <span className="ml-2 text-sm text-white/50">{u.email}</span>
                {!u.active && <span className="ml-2 rounded-full bg-red-500/20 px-2 py-0.5 text-xs text-red-300">Désactivé</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleActive(u)} data-testid={`staff-toggle-active-${u.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs hover:bg-white/20"><Power size={13} /> {u.active ? "Désactiver" : "Activer"}</button>
                <button onClick={() => remove(u)} data-testid={`staff-delete-${u.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 text-red-300 px-3 py-1 text-xs hover:bg-red-500/30"><Trash2 size={13} /> Supprimer</button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {PERMS.map((p) => (
                <PermToggle key={p.key} on={u.permissions.includes(p.key)} label={p.label} onClick={() => toggleUserPerm(u, p.key)} testid={`staff-perm-${u.id}-${p.key}`} />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input value={pwEdit[u.id] || ""} onChange={(e) => setPwEdit((s) => ({ ...s, [u.id]: e.target.value }))} className={`${input} max-w-xs`} placeholder="Nouveau mot de passe" data-testid={`staff-newpw-${u.id}`} />
              <button onClick={() => savePw(u)} className="rounded-full bg-white/10 px-3 py-2 text-xs hover:bg-white/20">Changer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
